import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { Button, FormControl, Input, Stack, Table, Tbody, Td, Text, Textarea, Th, Thead, Tr, useDisclosure, useToast, VStack } from "@chakra-ui/react";
import { FiFileText, FiImage, FiInfo, FiMessageSquare } from "react-icons/fi";

import { ModalFieldLabel, ModalFooterActions, PropertyModalSection, PropertyModalShell } from "./shared";
import TicketsTableRow from "../../Components/TableRows/TicketsTableRow";
import { agencyAddTicketAsync } from "../../redux/user/userSlice";
import { _arrayBufferToBase64 } from "../../utils/convertors";
import { getPropertyAddress } from "../../utils/verifier";
import Dropzone from "../Dropzone/Dropzone";

export default function TicketsTab({ property }) {
	const { t } = useTranslation();
	const isArchived = property.archived;
	const tickets = property.tickets.map((t) => {
		return { ...t, propertyId: property.id, address: getPropertyAddress(property) };
	});
	const user = useSelector((state) => state.user?.user);
	const author = { email: user.email, name: user.name, phoneNumber: user.phoneNumber };
	const initialAddTicketInformation = { title: "", description: "", media: [] };
	const [addTicketInformation, setAddTicketInformation] = useState(initialAddTicketInformation);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const toast = useToast();
	const dispatch = useDispatch();

	const handleChange = (e) => {
		setAddTicketInformation({ ...addTicketInformation, [e.target.name]: e.target.value });
	};

	const handleCloseModal = () => {
		onClose();
		setAddTicketInformation(initialAddTicketInformation);
	};

	const addTicket = () => {
		const payload = { ticket: addTicketInformation, propertyId: property.id, author };
		console.log(payload);
		dispatch(agencyAddTicketAsync(payload))
			.then((r) => {
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Document Added!",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				setAddTicketInformation(initialAddTicketInformation);
				onClose();
			});
	};

	const onDocumentDrop = async (acceptedFiles) => {
		console.log(acceptedFiles);
		let documents = [];
		for (const file of acceptedFiles) {
			const buffer = await file.arrayBuffer();
			const documentName = file.name;
			const contentType = file.type;
			documents.push({ documentData: _arrayBufferToBase64(buffer), documentName, contentType });
		}
		setAddTicketInformation({ ...addTicketInformation, media: documents });
	};

	const renderAddTicketModal = () => (
		<PropertyModalShell
			isOpen={isOpen}
			onClose={handleCloseModal}
			size="3xl"
			icon={FiMessageSquare}
			title={t("property.addTicket")}
			subtitle={t("property.addTicketSubtitle")}
			footer={<ModalFooterActions onCancel={handleCloseModal} onConfirm={addTicket} confirmLabel={t("misc.save")} />}
		>
			<PropertyModalSection icon={FiInfo} title={t("property.ticketInformation")}>
				<VStack spacing={3} align="stretch">
					<FormControl>
						<ModalFieldLabel icon={FiMessageSquare}>{t("table.title")}</ModalFieldLabel>
						<Input size="sm" onChange={handleChange} type="text" name="title" placeholder={t("property.titlePlaceholder")} borderRadius="md" />
					</FormControl>
					<FormControl>
						<ModalFieldLabel icon={FiFileText}>{t("table.description")}</ModalFieldLabel>
						<Textarea
							size="sm"
							onChange={handleChange}
							name="description"
							placeholder={t("property.descriptionPlaceholder")}
							borderRadius="md"
							rows={3}
						/>
					</FormControl>
					<FormControl>
						<ModalFieldLabel icon={FiImage}>{t("property.mediaFiles")}</ModalFieldLabel>
						<Dropzone onDrop={onDocumentDrop} accept={{ "image/*": [], "video/*": [] }} description={t("property.uploadImageOrVideo")} />
					</FormControl>
				</VStack>
			</PropertyModalSection>
		</PropertyModalShell>
	);

	return (
		<Stack>
			<Stack direction="row">
				<Button colorScheme="blue" onClick={onOpen} isDisabled={isArchived}>
					{t("property.addTicket")}
				</Button>
				{renderAddTicketModal()}
			</Stack>
			<Table variant="simple" size="sm">
				<Thead>
					<Tr my=".8rem" pl="0px">
						<Th>{t("table.addedDate")}</Th>
						<Th>{t("table.owner")}</Th>
						<Th pl="0px">{t("table.address")}</Th>
						<Th>{t("table.status")}</Th>
						<Th>{t("table.title")}</Th>
						<Th>{t("table.author")}</Th>
						<Th></Th>
					</Tr>
				</Thead>
				<Tbody>
					{tickets?.map((t, index) => (
						<TicketsTableRow key={index} row={t} author={author} owner={property.owner} />
					))}
					{tickets?.length === 0 && (
						<Tr>
							<Td colSpan={7}>{t("table.noData")}</Td>
						</Tr>
					)}
				</Tbody>
			</Table>
		</Stack>
	);
}
